package com.dotdrives.controller

import com.dotdrives.core.DotController
import com.dotdrives.core.uuidV7
import com.dotdrives.model.*
import com.dotdrives.repository.WorkflowRepository
import kotlinx.coroutines.*
import kotlinx.serialization.json.*

/** Account and organization scoped by the composition root. No optimistic provider success. */
data class WorkflowState(
    val busy: Boolean = false,
    val pending: WorkflowRequest? = null,
    val error: String? = null,
    val message: String? = null,
    val result: JsonObject? = null,
    val results: Map<String, JsonObject> = emptyMap(),
    val readErrors: Map<String, String> = emptyMap(),
    val jobs: List<WorkflowJob> = emptyList(),
    val jobsLoading: Boolean = false,
    val jobsError: String? = null,
)

class WorkflowController(
    private val repo: WorkflowRepository,
    scope: CoroutineScope,
    private val orgId: String,
    private val role: String,
    initialPending: WorkflowRequest? = null,
    private val restoreError: String? = null,
    private val savePending: (WorkflowRequest?) -> Unit = {},
) :
    DotController<WorkflowState>(
        WorkflowState(
            pending =
                initialPending?.takeIf {
                    it.body.text("org_id") == null || it.body.text("org_id") == orgId
                },
            error = restoreError,
        ),
        scope,
    ) {
    private val json = Json { ignoreUnknownKeys = true }
    private var jobsGeneration = 0
    private val readGenerations = mutableMapOf<String, Int>()

    fun read(endpoint: String, action: String = "status") {
        if (!canAdminister(role)) return
        val key = "$endpoint:$action"
        val generation = (readGenerations[key] ?: 0) + 1
        readGenerations[key] = generation
        scope.launch {
            try {
                var after: String? = null
                var merged: JsonObject? = null
                val rows = mutableListOf<JsonElement>()
                val collection =
                    if (action == "history") "history"
                    else if (endpoint.startsWith("qbo-")) "donations" else null
                do {
                    val value =
                        repo
                            .execute(
                                WorkflowRequest(
                                    endpoint,
                                    buildJsonObject {
                                        put("action", action)
                                        put("org_id", orgId)
                                        after?.let { put("after", it) }
                                    },
                                )
                            )
                            .getOrThrow()
                    if (readGenerations[key] != generation) return@launch
                    if (merged == null) merged = value
                    if (collection != null) rows += value[collection]?.jsonArray.orEmpty()
                    val next = value.text("next_after")
                    check(next == null || next != after) { "Unable to continue provider history." }
                    after = next
                } while (after != null)
                val value =
                    if (collection == null) requireNotNull(merged)
                    else JsonObject(requireNotNull(merged) + (collection to JsonArray(rows)))
                if (
                    endpoint == "billing-portal" &&
                        action == "status" &&
                        state.value.pending?.body?.text("action") == "purchase" &&
                        value.text("confirmed_purchase_operation_id") != null &&
                        value.text("confirmed_purchase_operation_id") == state.value.pending?.body?.text("operation_id")
                ) {
                    savePending(null)
                    update { it.copy(pending = null, error = null) }
                }
                update {
                    it.copy(results = it.results + (key to value), readErrors = it.readErrors - key)
                }
            } catch (e: Throwable) {
                if (e is CancellationException) throw e
                if (readGenerations[key] == generation)
                    update {
                        it.copy(
                            readErrors =
                                it.readErrors +
                                    (key to e.workflowMessage("Unable to load status. Retry."))
                        )
                    }
            }
        }
    }

    fun perform(endpoint: String, action: String, fields: Map<String, String> = emptyMap()) {
        if (restoreError != null) {
            update { it.copy(error = restoreError) }
            return
        }
        val financialAction =
            endpoint == "workflows" &&
                action in setOf("jobs.retry", "statement.request", "receipt.request", "drive.move", "manual.gift")
        val accepting = endpoint == "workflows" && action == "invite.accept"
        if (!accepting && !canAdminister(role) && !(financialAction && role == "member")) {
            update { it.copy(error = "An owner or administrator must make this change.") }
            return
        }
        if (state.value.busy || state.value.pending != null) {
            update { it.copy(error = "Retry the unfinished action before starting another.") }
            return
        }
        val request =
            WorkflowRequest(
                endpoint,
                buildJsonObject {
                    put("action", action)
                    if (
                        action !in
                            setOf("invite.accept", "invite.revoke", "invite.resend", "jobs.retry")
                    )
                        put("org_id", orgId)
                    fields.forEach { (key, value) ->
                        if (action in setOf("drive.move", "manual.gift") && key in setOf("amount", "gift_amount"))
                            put(key, value.toDouble())
                        else if (action == "statement.request" && key == "year")
                            put(key, value.toInt())
                        else put(key, value)
                    }
                    if (
                        endpoint in setOf("stripe-connect", "billing-portal") ||
                            action in setOf("drive.move", "manual.gift")
                    )
                        put("operation_id", uuidV7())
                    if (action == "invite.resend") put("request_id", uuidV7())
                    if (action == "invite.create") put("invite_id", uuidV7())
                },
            )
        try {
            savePending(request)
        } catch (_: Throwable) {
            update {
                it.copy(
                    error =
                        "This browser cannot save a safe retry. Enable session storage and try again."
                )
            }
            return
        }
        update { it.copy(pending = request) }
        retry()
    }

    fun retry() {
        if (state.value.busy) return
        val request = state.value.pending ?: return
        update { it.copy(busy = true, error = null, message = null, result = null) }
        scope.launch {
            repo
                .execute(request)
                .onSuccess { value ->
                    val awaitingPurchase =
                        request.endpoint == "billing-portal" &&
                            request.body.text("action") == "purchase" &&
                            value.text("url") != null
                    try {
                        if (!awaitingPurchase) savePending(null)
                    } catch (_: Throwable) {
                        update {
                            it.copy(
                                busy = false,
                                error =
                                    "The action completed but its saved retry could not be cleared. Retry to recover the same action.",
                            )
                        }
                        return@onSuccess
                    }
                    update {
                        it.copy(
                            busy = false,
                            pending = if (awaitingPurchase) request else null,
                            result = value,
                            message =
                                if (value.flag("accepted"))
                                    "Accepted; waiting for provider confirmation."
                                else "Action saved. Refresh to see current status.",
                        )
                    }
                    if (request.endpoint != "workflows")
                        read(
                            if (request.endpoint == "qbo-sync") "qbo-connect" else request.endpoint
                        )
                    else if (request.body.text("action") != "invite.accept") loadJobs()
                }
                .onFailure { e ->
                    if (e is CancellationException) throw e
                    val rejectedTeamTransaction =
                        e is WorkflowFailure &&
                            e.code in
                                setOf("invalid_request", "forbidden", "unauthorized", "conflict") &&
                            request.endpoint == "workflows" &&
                            request.body.text("action") in
                                setOf(
                                    "invite.create",
                                    "invite.accept",
                                    "invite.resend",
                                    "invite.revoke",
                                    "member.role",
                                    "member.deactivate",
                                    "drive.move",
                                )
                    val expiredPurchase =
                        e is WorkflowFailure &&
                            e.code == "checkout_expired" &&
                            request.endpoint == "billing-portal" &&
                            request.body.text("action") == "purchase"
                    // These team errors prove the single SQL transaction was rejected.
                    // Unknown/network/provider conflicts keep their frozen request identity.
                    if (rejectedTeamTransaction || expiredPurchase ||
                        (request.endpoint == "billing-portal" &&
                            (request.body.text("action") == "retry" ||
                                (e is WorkflowFailure && e.code == "billing_rejected")))) {
                        try {
                            savePending(null)
                            update { it.copy(pending = null) }
                        } catch (_: Throwable) {
                            /* Keep the original retry if persistence is unavailable. */
                        }
                    }
                    update {
                        it.copy(
                            busy = false,
                            error =
                                e.workflowMessage(
                                    "Unable to complete this action. Retry the saved request."
                                ),
                        )
                    }
                }
        }
    }

    fun loadJobs() {
        if (role !in setOf("owner", "admin", "member")) return
        val generation = ++jobsGeneration
        update { it.copy(jobsLoading = true, jobsError = null) }
        scope.launch {
            try {
                var after: String? = null
                val jobs = mutableListOf<WorkflowJob>()
                do {
                    val page =
                        repo
                            .execute(
                                WorkflowRequest(
                                    "workflows",
                                    buildJsonObject {
                                        put("action", "jobs.list")
                                        put("org_id", orgId)
                                        after?.let { put("after", it) }
                                    },
                                )
                            )
                            .getOrThrow()
                    jobs +=
                        json.decodeFromJsonElement<List<WorkflowJob>>(
                            page["jobs"] ?: JsonArray(emptyList())
                        )
                    val next = page.text("next_after")
                    check(next == null || next != after) { "Unable to continue delivery history." }
                    after = next
                } while (after != null && generation == jobsGeneration)
                if (generation == jobsGeneration)
                    update { it.copy(jobs = jobs, jobsLoading = false) }
            } catch (e: Throwable) {
                if (e is CancellationException) throw e
                if (generation == jobsGeneration)
                    update {
                        it.copy(
                            jobsLoading = false,
                            jobsError = e.workflowMessage("Unable to load delivery status. Retry."),
                        )
                    }
            }
        }
    }
}
